import {
  MAIN_SIDE_NAV_OVERLAY_TRAY_LIST_ITEM_ESTIMATED_HEIGHT,
  MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_GAP,
  MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_HEADER_ESTIMATED_HEIGHT,
} from '~/components/v2/components/workspace/main-side-nav/mainSideNavOverlayTrayVirtualRowSize';
import { groupWorkspaceImportRows } from '~/components/v2/components/workspace/workspace-imports/buildWorkspaceImportRows';
import {
  type WorkspaceImportRow,
  type WorkspaceImportsBanner,
} from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';

const BANNER_ESTIMATED_HEIGHT = 80;

export type WorkspaceImportsVirtualRow =
  | {
      banner: WorkspaceImportsBanner;
      type: 'banner';
    }
  | {
      row: WorkspaceImportRow;
      type: 'inProgress';
      withLeadingGap: boolean;
    }
  | {
      count: number;
      type: 'failedHeader';
      withSectionGap: boolean;
    }
  | {
      row: WorkspaceImportRow;
      type: 'failed';
    }
  | {
      count: number;
      type: 'cancelledHeader';
      withSectionGap: boolean;
    }
  | {
      row: WorkspaceImportRow;
      type: 'cancelled';
    }
  | {
      count: number;
      type: 'completedHeader';
      withSectionGap: boolean;
    }
  | {
      row: WorkspaceImportRow;
      type: 'completed';
    };

export type BuildWorkspaceImportsVirtualRowsParams = {
  banner: WorkspaceImportsBanner | null;
  rows: WorkspaceImportRow[];
};

const appendImportSection = (
  virtualRows: WorkspaceImportsVirtualRow[],
  sectionRows: WorkspaceImportRow[],
  headerType: 'cancelledHeader' | 'completedHeader' | 'failedHeader',
  itemType: 'cancelled' | 'completed' | 'failed',
) => {
  if (sectionRows.length === 0) {
    return;
  }

  virtualRows.push({
    count: sectionRows.length,
    type: headerType,
    withSectionGap: virtualRows.length > 0,
  });

  sectionRows.forEach((row) => {
    virtualRows.push({ row, type: itemType });
  });
};

export const buildWorkspaceImportsVirtualRows = ({
  banner,
  rows,
}: BuildWorkspaceImportsVirtualRowsParams): WorkspaceImportsVirtualRow[] => {
  const { cancelledRows, completedRows, failedRows, inProgressRows } = groupWorkspaceImportRows(rows);
  const virtualRows: WorkspaceImportsVirtualRow[] = [];

  if (banner) {
    virtualRows.push({ banner, type: 'banner' });
  }

  inProgressRows.forEach((row, index) => {
    virtualRows.push({
      row,
      type: 'inProgress',
      withLeadingGap: index === 0 && Boolean(banner),
    });
  });

  appendImportSection(virtualRows, failedRows, 'failedHeader', 'failed');
  appendImportSection(virtualRows, cancelledRows, 'cancelledHeader', 'cancelled');
  appendImportSection(virtualRows, completedRows, 'completedHeader', 'completed');

  return virtualRows;
};

export const getWorkspaceImportsVirtualRowKey = (row: WorkspaceImportsVirtualRow) => {
  switch (row.type) {
    case 'banner':
      return 'banner';
    case 'inProgress':
      return `in-progress-${row.row.id}`;
    case 'failedHeader':
      return 'failed-header';
    case 'failed':
      return `failed-${row.row.id}`;
    case 'cancelledHeader':
      return 'cancelled-header';
    case 'cancelled':
      return `cancelled-${row.row.id}`;
    case 'completedHeader':
      return 'completed-header';
    case 'completed':
      return `completed-${row.row.id}`;
  }
};

export const estimateWorkspaceImportsVirtualRowSize = (row: WorkspaceImportsVirtualRow) => {
  switch (row.type) {
    case 'banner':
      return BANNER_ESTIMATED_HEIGHT;
    case 'inProgress':
      return row.withLeadingGap
        ? MAIN_SIDE_NAV_OVERLAY_TRAY_LIST_ITEM_ESTIMATED_HEIGHT + MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_GAP
        : MAIN_SIDE_NAV_OVERLAY_TRAY_LIST_ITEM_ESTIMATED_HEIGHT;
    case 'failedHeader':
    case 'cancelledHeader':
    case 'completedHeader':
      return row.withSectionGap
        ? MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_HEADER_ESTIMATED_HEIGHT + MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_GAP
        : MAIN_SIDE_NAV_OVERLAY_TRAY_SECTION_HEADER_ESTIMATED_HEIGHT;
    default:
      return MAIN_SIDE_NAV_OVERLAY_TRAY_LIST_ITEM_ESTIMATED_HEIGHT;
  }
};
